import React, { useState } from "react";
import { CodeSnippet } from "../../../components/organisms/CodeSnippet";
import { Api } from "../../../../common/service/Api";
import { usePrimaryOrganization, useSelf } from "../../../hooks";
import { useCreateDefaultCollectionGroups } from "../../../hooks/useCollectionGroups";
import { Field, Form } from "react-final-form";
import clsx from "clsx";
import {
  Button,
  OnboardingOrganizationDropdown,
  StepNumberCircle,
  TextInput,
} from "@truevault/aquilo";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { Routes } from "../../../root/routes";
import { OmniBarProvider } from "../../../../common/hooks/useOmniBar";
import { OmniBar } from "../../../components/dialog/OmniBar";
import { DescriptionCheckbox } from "../../../components/input/DescriptionCheckbox";
import { KeyboardArrowLeft } from "@mui/icons-material";
import { PrivacyCenterDto } from "../../../../common/service/server/dto/PrivacyCenterDto";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import {
  DomainSetupCard,
  DomainSetupProgress,
} from "../../../components/superorg/settings/DomainSetupCard";
import { getPrivacyCenterCnameValue } from "../../../util/privacyCenterUtil";
import { DeveloperSetupShareCard } from "../../../components/privacyCenter/DeveloperSetupShareCard";
import { DomainSetupDocumentationLink } from "../../../components/superorg/settings/DomainSetupInstructionPrimitives";
import { useProductAnalytics } from "../../../analytics/productAnalytics";
import { isDomainSetupComplete } from "../../../util/domainSetupNavigation";

export function DomainSetupPage() {
  const captureProductAnalyticsEvent = useProductAnalytics({
    flow: "privacy_email_domain_setup",
  });
  const history = useHistory();
  const [org] = usePrimaryOrganization();
  const [self] = useSelf();

  const organizationId = org?.id;

  const { create: createDefaultCollectionGroups } =
    useCreateDefaultCollectionGroups(organizationId);

  const [domainProgress, setDomainProgress] = React.useState<DomainSetupProgress>({
    ready: false,
    hasSavedDomain: false,
    canProceed: false,
    verificationDisabled: true,
    logoUploaded: false,
    testEmailComplete: false,
  });
  const setupComplete = isDomainSetupComplete(domainProgress);
  return (
    <OmniBarProvider>
      <OmniBar />
      <div className="relative min-h-screen bg-[#f9f8fb] overflow-hidden">
        <div className="tw-boundary relative z-10">
          <div className="absolute right-0 top-[177px] z-0 pointer-events-none" aria-hidden="true">
            <svg
              width="293"
              height="48"
              viewBox="0 0 293 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M56.8103 11.7928L3.04378 0.194451L2.34503 0.0440731C1.9892 -0.0332102 1.61879 -0.00849442 1.27638 0.115379C0.933968 0.239253 0.633472 0.457246 0.409447 0.744304C0.185423 1.03136 0.0469608 1.37582 0.00999465 1.73807C-0.0269715 2.10031 0.0390622 2.46564 0.200486 2.79203L21.548 46.1438C21.6861 46.4223 21.8892 46.6635 22.1401 46.8472C22.3909 47.0309 22.6822 47.1516 22.9895 47.1992C23.2977 47.2472 23.6129 47.2193 23.908 47.1182C24.203 47.017 24.469 46.8455 24.683 46.6185L39.5104 30.9842C39.6159 30.8702 39.7438 30.7792 39.8862 30.7171C40.0285 30.6549 40.1822 30.6229 40.3375 30.623C40.5487 30.62 40.7566 30.6745 40.9392 30.7806C41.1217 30.8868 41.2719 31.0406 41.3738 31.2255L44.4109 36.8431C44.6048 37.2176 44.9184 37.5166 45.3018 37.6923C45.6852 37.868 46.1164 37.9104 46.5267 37.8127L46.5418 37.8093C46.9571 37.7214 47.3307 37.4963 47.6027 37.1703C47.8746 36.8444 48.0291 36.4365 48.0412 36.0121L48.6496 23.0448C48.6568 22.883 48.6972 22.7244 48.7683 22.5789C48.8446 22.4348 48.9481 22.307 49.0731 22.2025L57.6272 15.1806C57.9084 14.9498 58.1188 14.6445 58.2345 14.2996C58.3501 13.9547 58.3662 13.5842 58.2809 13.2306C58.1956 12.877 58.0125 12.5545 57.7524 12.3002C57.4923 12.0459 57.1658 11.87 56.8103 11.7928ZM57.1387 14.5837L48.5847 21.6056C48.4732 21.6954 48.3722 21.7974 48.2835 21.9098C48.2036 22.0098 48.1346 22.118 48.0777 22.2326C48.018 22.3499 47.971 22.4731 47.9374 22.6003L47.9382 22.6041C47.9028 22.7366 47.8823 22.8726 47.8773 23.0097L47.2691 35.977C47.264 36.232 47.1723 36.4776 47.009 36.6735C46.8458 36.8694 46.6207 37.004 46.3708 37.055L46.3633 37.0567C46.1171 37.1178 45.8574 37.094 45.6264 36.9891C45.3955 36.8842 45.2067 36.7044 45.0907 36.4788L42.0528 30.8574C41.8963 30.5683 41.6685 30.3239 41.391 30.1476C41.1135 29.9712 40.7956 29.8686 40.4673 29.8497L40.4636 29.8505C40.4195 29.8484 40.3755 29.8463 40.3323 29.8479C40.0261 29.848 39.7244 29.9229 39.4537 30.0661C39.2645 30.1644 39.0937 30.2947 38.9488 30.451L24.1215 46.0853C23.9928 46.2206 23.8334 46.323 23.6567 46.3835C23.4801 46.4441 23.2914 46.4611 23.1067 46.4332C22.922 46.4052 22.7469 46.3331 22.596 46.2229C22.4452 46.1128 22.3232 45.9678 22.2404 45.8004L0.892881 2.44873C0.797446 2.25286 0.758944 2.03411 0.781768 1.81742C0.804591 1.60073 0.887817 1.39481 1.02197 1.22311C1.15612 1.05142 1.3358 0.920861 1.54054 0.846306C1.74527 0.771752 1.96683 0.756198 2.17998 0.801424L2.89531 0.956003L56.6451 12.5502C56.8585 12.5964 57.0546 12.7017 57.2109 12.8542C57.3672 13.0067 57.4774 13.2001 57.5289 13.4123C57.5805 13.6245 57.5712 13.8469 57.5022 14.0541C57.4332 14.2612 57.3072 14.4448 57.1387 14.5837Z"
                fill="#5331C6"
              />
              <path
                d="M48.4552 21.9836L48.1357 22.688L47.9384 22.601L47.9375 22.5972L2.63649 2.05591L40.4638 29.8474L40.4675 29.8466L40.5676 29.9234L40.1115 30.5471L39.4538 30.063L2.17669 2.68042C2.03009 2.57497 1.92496 2.4216 1.87947 2.24684C1.83398 2.07208 1.85099 1.88691 1.92757 1.72336C2.00415 1.55982 2.13548 1.42818 2.29884 1.35121C2.4622 1.27424 2.64733 1.25678 2.8222 1.30185C2.86504 1.31231 2.90676 1.32695 2.94674 1.34556L48.2837 21.9067L48.4552 21.9836Z"
                fill="#5331C6"
              />
              <path
                d="M52.2241 37.3805C64.3661 41.8775 75.5711 46.9085 88.8539 46.9085C98.4514 46.9085 108.654 43.7144 117.332 39.6037C126.484 35.2685 135.523 29.5138 143.904 23.5473C149.588 19.5016 155.861 16.5989 162.255 13.8782C172.982 9.31344 184.122 8.1903 195.708 6.92631C210.956 5.26289 226.203 4.54116 241.548 5.02071C252.205 5.35373 263.123 6.99281 273.626 8.65546C279.52 9.58855 287.725 10.0995 292.964 13.243"
                stroke="#5432D0"
                strokeLinecap="round"
                strokeDasharray="6 6"
              />
            </svg>
          </div>
          <div className="absolute left-0 top-[819px] z-0 pointer-events-none" aria-hidden="true">
            <svg
              width="296"
              height="48"
              viewBox="0 0 296 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M239.19 11.7928L292.956 0.194451L293.655 0.0440731C294.011 -0.0332102 294.381 -0.00849442 294.724 0.115379C295.066 0.239253 295.367 0.457246 295.591 0.744304C295.815 1.03136 295.953 1.37582 295.99 1.73807C296.027 2.10031 295.961 2.46564 295.8 2.79203L274.452 46.1438C274.314 46.4223 274.111 46.6635 273.86 46.8472C273.609 47.0309 273.318 47.1516 273.011 47.1992C272.702 47.2472 272.387 47.2193 272.092 47.1182C271.797 47.017 271.531 46.8455 271.317 46.6185L256.49 30.9842C256.384 30.8702 256.256 30.7792 256.114 30.7171C255.971 30.6549 255.818 30.6229 255.662 30.623C255.451 30.62 255.243 30.6745 255.061 30.7806C254.878 30.8868 254.728 31.0406 254.626 31.2255L251.589 36.8431C251.395 37.2176 251.082 37.5166 250.698 37.6923C250.315 37.868 249.884 37.9104 249.473 37.8127L249.458 37.8093C249.043 37.7214 248.669 37.4963 248.397 37.1703C248.125 36.8444 247.971 36.4365 247.959 36.0121L247.35 23.0448C247.343 22.883 247.303 22.7244 247.232 22.5789C247.155 22.4348 247.052 22.307 246.927 22.2025L238.373 15.1806C238.092 14.9498 237.881 14.6445 237.766 14.2996C237.65 13.9547 237.634 13.5842 237.719 13.2306C237.804 12.877 237.988 12.5545 238.248 12.3002C238.508 12.0459 238.834 11.87 239.19 11.7928ZM238.861 14.5837L247.415 21.6056C247.527 21.6954 247.628 21.7974 247.716 21.9098C247.796 22.0098 247.865 22.118 247.922 22.2326C247.982 22.3499 248.029 22.4731 248.063 22.6003L248.062 22.6041C248.097 22.7366 248.118 22.8726 248.123 23.0097L248.731 35.977C248.736 36.232 248.828 36.4776 248.991 36.6735C249.154 36.8694 249.379 37.004 249.629 37.055L249.637 37.0567C249.883 37.1178 250.143 37.094 250.374 36.9891C250.605 36.8842 250.793 36.7044 250.909 36.4788L253.947 30.8574C254.104 30.5683 254.332 30.3239 254.609 30.1476C254.886 29.9712 255.204 29.8686 255.533 29.8497L255.536 29.8505C255.581 29.8484 255.625 29.8463 255.668 29.8479C255.974 29.848 256.276 29.9229 256.546 30.0661C256.735 30.1644 256.906 30.2947 257.051 30.451L271.878 46.0853C272.007 46.2206 272.167 46.323 272.343 46.3835C272.52 46.4441 272.709 46.4611 272.893 46.4332C273.078 46.4052 273.253 46.3331 273.404 46.2229C273.555 46.1128 273.677 45.9678 273.76 45.8004L295.107 2.44873C295.203 2.25286 295.241 2.03411 295.218 1.81742C295.195 1.60073 295.112 1.39481 294.978 1.22311C294.844 1.05142 294.664 0.920861 294.459 0.846306C294.255 0.771752 294.033 0.756198 293.82 0.801424L293.105 0.956003L239.355 12.5502C239.142 12.5964 238.945 12.7017 238.789 12.8542C238.633 13.0067 238.523 13.2001 238.471 13.4123C238.42 13.6245 238.429 13.8469 238.498 14.0541C238.567 14.2612 238.693 14.4448 238.861 14.5837Z"
                fill="#5331C6"
              />
              <path
                d="M247.545 21.9836L247.864 22.688L248.062 22.601L248.062 22.5972L293.364 2.05591L255.536 29.8474L255.533 29.8466L255.432 29.9234L255.888 30.5471L256.546 30.063L293.823 2.68042C293.97 2.57497 294.075 2.4216 294.121 2.24684C294.166 2.07208 294.149 1.88691 294.072 1.72336C293.996 1.55982 293.865 1.42818 293.701 1.35121C293.538 1.27424 293.353 1.25678 293.178 1.30185C293.135 1.31231 293.093 1.32695 293.053 1.34556L247.716 21.9067L247.545 21.9836Z"
                fill="#5331C6"
              />
              <path
                d="M243.776 37.3805C231.634 41.8775 220.429 46.9085 207.147 46.9085C197.549 46.9085 187.347 43.7144 178.669 39.6037C169.516 35.2685 160.478 29.5138 152.096 23.5473C146.413 19.5016 140.139 16.5989 133.746 13.8782C123.019 9.31344 111.879 8.1903 100.292 6.92631C85.0442 5.26289 69.7977 4.54116 54.4521 5.02071C43.7954 5.35373 32.8776 6.99281 22.3746 8.65546C16.4801 9.58855 8.27545 10.0995 3.03632 13.243"
                stroke="#5432D0"
                strokeLinecap="round"
                strokeDasharray="6 6"
              />
            </svg>
          </div>
          <div className="flex justify-end pr-[32px] pt-[12px]">
            <OnboardingOrganizationDropdown
              orgName={org?.name}
              userDisplayName={`${self?.firstName} ${self?.lastName}`}
              showSettings={false}
            />
          </div>
          <div className="mx-auto flex flex-col items-center px-[16px] pt-[50px]">
            <div className="flex w-[700px] max-w-full items-center flex-col">
              <Link to={Routes.getCompliant.dashboard} aria-label="Go to dashboard">
                <img
                  src="/assets/images/nav-icon-truevault.png"
                  alt="TrueVault Logo"
                  className="w-[40px] h-[40px]"
                />
              </Link>
              <div className="mt-[12px] w-[627px] max-w-full text-center text-[30px] font-bold leading-[45px] text-primary-800">
                Let’s set up your privacy email and a custom domain for your Privacy Center
              </div>
              <div className="mt-[12px] w-[645px] max-w-full text-center text-[20px] font-medium leading-[30px] text-primary-800">
                Your privacy email will be used to receive and respond to privacy requests received
                from your new Privacy Center site where users can submit privacy requests.
              </div>
            </div>
            <div className="relative mt-[24px] w-[890px] max-w-full">
              <DomainSetupCard
                className="max-w-full"
                onStateChange={setDomainProgress}
                trackOnboardingAnalytics
              />
              <div
                className="absolute bottom-[10px] left-[calc(100%+101px)] h-[281px] w-[181px] pointer-events-none"
                aria-hidden="true"
              >
                <svg
                  width="181"
                  height="281"
                  viewBox="0 0 181 281"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M178.079 18.3924L63.3723 0.372365C62.7437 0.274175 62.1013 0.429186 61.586 0.803368C61.0708 1.17755 60.7248 1.74032 60.6241 2.36815L42.8003 114.073C42.7007 114.701 42.8545 115.342 43.228 115.856C43.6016 116.37 44.1643 116.714 44.7927 116.813L54.85 118.393L54.9579 117.717L44.9006 116.137C44.4517 116.066 44.0498 115.82 43.7829 115.453C43.5161 115.086 43.4061 114.628 43.4772 114.179L61.301 2.47448C61.3731 2.026 61.6203 1.62401 61.9883 1.35669C62.3564 1.08938 62.8153 0.978582 63.2644 1.04859L177.971 19.0686C178.421 19.1391 178.823 19.385 179.091 19.7522C179.358 20.1194 179.469 20.5779 179.398 21.027L161.574 132.732C161.521 133.062 161.372 133.37 161.146 133.617C160.944 133.833 160.692 133.997 160.412 134.092C160.153 134.178 159.877 134.2 159.608 134.157L149.55 132.577L149.443 133.254L159.5 134.833C159.842 134.887 160.193 134.866 160.527 134.772C160.861 134.677 161.171 134.511 161.435 134.286C161.457 134.268 161.477 134.249 161.497 134.228C161.666 134.078 161.811 133.903 161.928 133.709C162.093 133.443 162.202 133.147 162.251 132.838L180.074 21.1333C180.174 20.5048 180.02 19.8632 179.646 19.3492C179.272 18.8353 178.708 18.4911 178.079 18.3924Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M178.598 28.2036L59.8252 9.54492L59.7172 10.2219L178.49 28.8806L178.598 28.2036Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M65.9647 8.06682C67.0874 8.24319 68.1426 7.47687 68.3216 6.35519C68.5005 5.23352 67.7355 4.18125 66.6129 4.00489C65.4902 3.82852 64.435 4.59484 64.256 5.71651C64.077 6.83819 64.8421 7.89046 65.9647 8.06682Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M71.8085 8.98674C72.9311 9.16311 73.9863 8.39679 74.1653 7.27512C74.3443 6.15344 73.5793 5.10118 72.4566 4.92481C71.3339 4.74844 70.2787 5.51476 70.0998 6.63644C69.9208 7.75811 70.6858 8.81038 71.8085 8.98674Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M77.6532 9.90471C78.7759 10.0811 79.8311 9.31476 80.01 8.19308C80.189 7.07141 79.424 6.01914 78.3013 5.84278C77.1787 5.66641 76.1235 6.43273 75.9445 7.55441C75.7655 8.67608 76.5305 9.72835 77.6532 9.90471Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M145.388 39.0519L88.0349 30.0419C87.0977 29.8947 86.2583 29.3815 85.7012 28.6153C85.1442 27.8491 84.9152 26.8926 85.0646 25.9563C85.214 25.0199 85.7296 24.1804 86.4979 23.6224C87.2662 23.0644 88.2244 22.8337 89.1616 22.9809L146.515 31.9909C147.452 32.1381 148.292 32.6513 148.849 33.4175C149.406 34.1837 149.635 35.1402 149.485 36.0765C149.336 37.0128 148.82 37.8524 148.052 38.4103C147.284 38.9683 146.326 39.1991 145.388 39.0519ZM89.0749 23.5241C88.2819 23.3995 87.4712 23.5947 86.821 24.0669C86.1709 24.539 85.7347 25.2494 85.6082 26.0417C85.4818 26.834 85.6756 27.6433 86.1469 28.2916C86.6183 28.9399 87.3286 29.3742 88.1216 29.4987L145.475 38.5087C146.268 38.6333 147.079 38.438 147.729 37.9659C148.379 37.4937 148.815 36.7834 148.942 35.9911C149.068 35.1988 148.874 34.3895 148.403 33.7412C147.932 33.0928 147.221 32.6586 146.428 32.534L89.0749 23.5241Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M152.061 90.6554L65.6231 77.0763C64.3982 76.8825 63.3013 76.2111 62.5732 75.2095C61.845 74.2079 61.5449 72.9579 61.7389 71.7336L66.1588 44.0328C66.3555 42.809 67.0301 41.7121 68.0345 40.9826C69.0388 40.2532 70.2911 39.9509 71.5164 40.1419L157.954 53.721C159.179 53.9148 160.276 54.5862 161.004 55.5878C161.733 56.5894 162.033 57.8395 161.839 59.0637L157.419 86.7645C157.222 87.9883 156.547 89.0853 155.543 89.8147C154.539 90.5441 153.286 90.8465 152.061 90.6554ZM71.4297 40.6851C70.3486 40.5165 69.2437 40.7833 68.3574 41.4269C67.4712 42.0705 66.876 43.0384 66.7025 44.1182L62.2825 71.819C62.1114 72.8992 62.3761 74.0022 63.0186 74.886C63.6612 75.7697 64.629 76.3621 65.7098 76.5332L152.148 90.1122C153.229 90.2808 154.334 90.014 155.22 89.3704C156.106 88.7268 156.701 87.7589 156.875 86.6791L161.295 58.9783C161.466 57.8981 161.201 56.7951 160.559 55.9113C159.916 55.0276 158.949 54.4352 157.868 54.2641L71.4297 40.6851Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M93.8372 56.8916L124.009 61.6315C124.507 61.7097 124.954 61.9826 125.25 62.3898C125.546 62.797 125.667 63.3057 125.588 63.8032C125.509 64.3007 125.235 64.7472 124.826 65.0438C124.418 65.3403 123.908 65.463 123.41 65.3848L93.2383 60.6449C92.7402 60.5666 92.2937 60.2937 91.9976 59.8865C91.7016 59.4793 91.5801 58.9707 91.6595 58.4731C91.7388 57.9756 92.0128 57.5291 92.4211 57.2326C92.8294 56.936 93.3391 56.8133 93.8372 56.8916Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M92.6683 64.2216L150.838 73.3598C151.336 73.4381 151.781 73.711 152.077 74.118C152.374 74.5253 152.495 75.0338 152.416 75.5314C152.336 76.0289 152.062 76.4754 151.654 76.772C151.246 77.0683 150.737 77.1912 150.239 77.1131L92.0694 67.975C91.5712 67.8967 91.1247 67.6238 90.8287 67.2166C90.5326 66.8094 90.4111 66.3008 90.4905 65.8032C90.5699 65.3057 90.8438 64.8592 91.2521 64.5626C91.6605 64.2661 92.1701 64.1434 92.6683 64.2216Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M79.6906 52.3037C84.0304 52.9857 86.9873 57.0534 86.2957 61.389C85.6038 65.7248 81.525 68.688 77.1851 68.0064C72.845 67.3246 69.8874 63.2559 70.5792 58.92C71.2712 54.5844 75.3507 51.6219 79.6906 52.3037Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M149.98 103.692L63.5418 90.1126C62.3165 89.9219 61.0645 90.2244 60.0602 90.9538C59.0559 91.6831 58.3813 92.7799 58.1842 94.0035L53.7642 121.704C53.5707 122.929 53.8709 124.178 54.599 125.18C55.3271 126.181 56.4238 126.853 57.6485 127.047L144.086 140.626C145.312 140.817 146.564 140.515 147.569 139.786C148.573 139.056 149.247 137.959 149.444 136.735L153.864 109.034C154.058 107.81 153.758 106.56 153.03 105.558C152.302 104.557 151.205 103.885 149.98 103.692ZM148.9 136.65C148.727 137.73 148.132 138.698 147.246 139.342C146.359 139.985 145.254 140.252 144.173 140.083L57.7352 126.504C56.6544 126.333 55.6867 125.74 55.0442 124.857C54.4017 123.973 54.1369 122.87 54.3079 121.79L58.7279 94.0889C58.9015 93.0091 59.4967 92.0413 60.3829 91.3977C61.2691 90.7541 62.374 90.4873 63.4551 90.6558L149.893 104.235C150.974 104.406 151.942 104.998 152.585 105.882C153.227 106.766 153.492 107.869 153.32 108.949L148.9 136.65Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M85.8636 106.862L116.036 111.602C116.534 111.68 116.98 111.953 117.276 112.361C117.572 112.768 117.694 113.276 117.614 113.774C117.535 114.271 117.261 114.718 116.853 115.014C116.444 115.311 115.935 115.434 115.437 115.355L85.2647 110.616C84.7666 110.537 84.32 110.264 84.024 109.857C83.7279 109.45 83.6065 108.941 83.6858 108.444C83.7652 107.946 84.0392 107.5 84.4474 107.203C84.8558 106.907 85.3654 106.784 85.8636 106.862Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M84.6946 114.192L142.864 123.331C143.362 123.409 143.808 123.682 144.104 124.089C144.4 124.496 144.521 125.005 144.442 125.502C144.363 126 144.089 126.446 143.68 126.743C143.272 127.039 142.763 127.162 142.265 127.084L84.0958 117.946C83.5976 117.867 83.1511 117.594 82.855 117.187C82.559 116.78 82.4375 116.271 82.5169 115.774C82.5963 115.276 82.8702 114.83 83.2785 114.533C83.6869 114.237 84.1965 114.114 84.6946 114.192Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M71.717 102.274C76.0568 102.956 79.0136 107.024 78.322 111.36C77.6302 115.695 73.5514 118.659 69.2115 117.977C64.8714 117.295 61.9138 113.227 62.6056 108.891C63.2976 104.555 67.377 101.593 71.717 102.274Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M91.1128 149.484L4.62364 157.234C3.39806 157.346 2.26704 157.942 1.47852 158.892C0.689997 159.843 0.30833 161.07 0.417195 162.304L2.92037 190.238C3.0327 191.472 3.62642 192.612 4.57135 193.407C5.51628 194.202 6.73531 194.587 7.9612 194.479L94.4504 186.729C95.6762 186.618 96.8075 186.022 97.5961 185.071C98.3847 184.121 98.7662 182.893 98.6568 181.659L96.1537 153.725C96.0418 152.491 95.4482 151.351 94.5032 150.556C93.5581 149.761 92.3388 149.375 91.1128 149.484ZM98.1129 181.708C98.2096 182.797 97.8731 183.88 97.1772 184.719C96.4814 185.557 95.483 186.083 94.4013 186.181L7.91212 193.932C6.83041 194.027 5.75467 193.687 4.92085 192.985C4.08704 192.284 3.56323 191.278 3.46432 190.189L0.961153 162.255C0.864875 161.166 1.20156 160.083 1.89735 159.245C2.59315 158.406 3.59124 157.88 4.67272 157.782L91.1619 150.031C92.2437 149.935 93.3197 150.276 94.1536 150.977C94.9876 151.679 95.5112 152.685 95.6097 153.774L98.1129 181.708Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M30.1894 168.08L60.379 165.375C60.8761 165.33 61.3714 165.486 61.7553 165.809C62.1392 166.132 62.3806 166.596 62.4258 167.098C62.4708 167.601 62.3157 168.101 61.9953 168.487C61.6749 168.873 61.2153 169.115 60.7181 169.16L30.5286 171.865C30.0314 171.909 29.5362 171.753 29.1523 171.43C28.7682 171.107 28.5267 170.643 28.4817 170.14C28.4368 169.638 28.5919 169.139 28.9123 168.753C29.2327 168.366 29.6923 168.125 30.1894 168.08Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M30.851 175.467L89.0544 170.251C89.5515 170.207 90.0467 170.363 90.4307 170.686C90.8146 171.009 91.056 171.473 91.1011 171.975C91.1461 172.477 90.9911 172.977 90.6706 173.364C90.3502 173.75 89.8906 173.991 89.3935 174.036L31.1902 179.252C30.6931 179.296 30.1978 179.14 29.8139 178.817C29.4299 178.494 29.1884 178.029 29.1433 177.527C29.0984 177.025 29.2536 176.525 29.5739 176.139C29.8943 175.753 30.3539 175.511 30.851 175.467Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M15.457 167.052C19.7961 166.663 23.6346 169.891 24.0266 174.265C24.4184 178.639 21.2149 182.497 16.8758 182.886C12.5366 183.275 8.69748 180.048 8.30537 175.674C7.91337 171.299 11.1176 167.441 15.457 167.052Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M162.626 243.43L79.786 217.392C78.6114 217.025 77.3382 217.141 76.2453 217.716C75.1524 218.291 74.3291 219.278 73.9558 220.459L65.5463 247.215C65.1764 248.397 65.2873 249.677 65.8548 250.774C66.4222 251.871 67.3998 252.695 68.5733 253.066L151.413 279.103C152.588 279.471 153.862 279.355 154.955 278.78C156.048 278.205 156.871 277.218 157.244 276.036L165.653 249.281C166.024 248.098 165.913 246.818 165.345 245.721C164.778 244.624 163.8 243.8 162.626 243.43ZM156.723 275.872C156.394 276.915 155.668 277.786 154.703 278.294C153.739 278.801 152.615 278.904 151.578 278.579L68.7382 252.541C67.7026 252.214 66.8399 251.487 66.3392 250.519C65.8385 249.551 65.7407 248.422 66.0673 247.378L74.4768 220.623C74.806 219.58 75.5324 218.71 76.4968 218.202C77.4611 217.695 78.5847 217.592 79.6211 217.917L162.461 243.954C163.497 244.281 164.36 245.008 164.861 245.976C165.362 246.944 165.459 248.073 165.132 249.117L156.723 275.872Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M99.2315 237.218L128.147 246.307C128.623 246.457 129.021 246.791 129.251 247.236C129.482 247.682 129.527 248.203 129.376 248.684C129.225 249.165 128.889 249.568 128.445 249.801C128.001 250.035 127.484 250.082 127.008 249.932L98.0921 240.843C97.6159 240.694 97.2185 240.359 96.988 239.914C96.7574 239.468 96.7126 238.947 96.8639 238.465C97.0152 237.984 97.3501 237.583 97.7941 237.349C98.2382 237.115 98.7553 237.069 99.2315 237.218Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M97.0069 244.297L152.755 261.819C153.231 261.968 153.628 262.303 153.859 262.748C154.089 263.194 154.134 263.715 153.983 264.196C153.832 264.677 153.497 265.079 153.053 265.313C152.608 265.546 152.091 265.593 151.615 265.444L95.8675 247.921C95.3913 247.772 94.9939 247.438 94.7634 246.992C94.5328 246.546 94.488 246.025 94.6393 245.543C94.7906 245.062 95.1255 244.661 95.5695 244.427C96.0136 244.194 96.5307 244.147 97.0069 244.297Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M86.0205 230.617C90.1765 231.924 92.4823 236.377 91.1654 240.567C89.8484 244.757 85.4097 247.09 81.2536 245.783C77.0974 244.477 74.7907 240.024 76.1075 235.834C77.4244 231.645 81.8642 229.311 86.0205 230.617Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                </svg>
              </div>
              <div
                className="absolute bottom-[-120px] left-[-282px] h-[259px] w-[181px] pointer-events-none"
                aria-hidden="true"
              >
                <svg
                  width="181"
                  height="259"
                  viewBox="0 0 181 259"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.62895 18.3855L117.378 0.372226C118.007 0.274072 118.65 0.429025 119.165 0.803067C119.681 1.17711 120.027 1.73967 120.128 2.36726L137.958 114.03C138.058 114.658 137.904 115.299 137.53 115.813C137.156 116.326 136.593 116.671 135.965 116.77L125.904 118.349L125.796 117.673L135.857 116.094C136.306 116.023 136.708 115.777 136.975 115.41C137.242 115.043 137.352 114.585 137.281 114.137L119.45 2.47356C119.378 2.02524 119.131 1.6234 118.763 1.35619C118.395 1.08897 117.935 0.978216 117.486 1.0482L2.73689 19.0614C2.28733 19.132 1.88456 19.3778 1.61706 19.7448C1.34955 20.1119 1.23916 20.5702 1.31012 21.0191L19.1405 132.682C19.193 133.012 19.3421 133.32 19.5689 133.567C19.7701 133.783 20.0223 133.946 20.3025 134.041C20.5618 134.127 20.8379 134.15 21.1074 134.107L31.1684 132.528L31.2763 133.204L21.2153 134.783C20.8725 134.837 20.522 134.816 20.1878 134.721C19.8537 134.627 19.5439 134.461 19.2799 134.235C19.2578 134.218 19.237 134.199 19.2179 134.178C19.0487 134.028 18.9033 133.853 18.7865 133.659C18.6217 133.393 18.5118 133.097 18.4635 132.788L0.633115 21.1254C0.53353 20.4972 0.687793 19.8557 1.06202 19.342C1.43625 18.8283 1.99983 18.4842 2.62895 18.3855Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M2.11037 28.1927L120.927 9.54102L121.035 10.2178L2.21843 28.8694L2.11037 28.1927Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M114.785 8.06518C113.662 8.24148 112.606 7.47545 112.427 6.35419C112.248 5.23294 113.013 4.18107 114.136 4.00477C115.259 3.82847 116.315 4.5945 116.494 5.71575C116.673 6.83701 115.908 7.88888 114.785 8.06518Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M108.939 8.98315C107.815 9.15945 106.76 8.39342 106.581 7.27216C106.402 6.15091 107.167 5.09904 108.29 4.92274C109.413 4.74644 110.469 5.51247 110.648 6.63372C110.827 7.75498 110.062 8.80685 108.939 8.98315Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M103.092 9.90112C101.969 10.0774 100.913 9.31138 100.734 8.19013C100.555 7.06888 101.32 6.01701 102.444 5.8407C103.567 5.6644 104.622 6.43044 104.801 7.55169C104.98 8.67294 104.215 9.72482 103.092 9.90112Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M35.3317 39.0359L92.7064 30.0293C93.6439 29.8821 94.4837 29.3691 95.0409 28.6032C95.5982 27.8373 95.8273 26.8812 95.6778 25.9452C95.5283 25.0092 95.0126 24.17 94.244 23.6122C93.4753 23.0544 92.5168 22.8238 91.5793 22.9709L34.2046 31.9775C33.2671 32.1247 32.4274 32.6377 31.8701 33.4036C31.3129 34.1695 31.0838 35.1256 31.2332 36.0616C31.3827 36.9976 31.8985 37.8368 32.6671 38.3946C33.4357 38.9524 34.3942 39.1831 35.3317 39.0359ZM91.666 23.5139C92.4593 23.3893 93.2703 23.5845 93.9207 24.0565C94.5711 24.5285 95.0075 25.2386 95.134 26.0306C95.2604 26.8226 95.0666 27.6316 94.5951 28.2797C94.1235 28.9277 93.413 29.3618 92.6197 29.4863L35.245 38.4929C34.4517 38.6175 33.6407 38.4223 32.9903 37.9503C32.34 37.4783 31.9035 36.7683 31.7771 35.9763C31.6506 35.1843 31.8445 34.3752 32.316 33.7272C32.7875 33.0791 33.498 32.645 34.2913 32.5205L91.666 23.5139Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M28.6565 90.6206L115.126 77.0466C116.352 76.8528 117.449 76.1817 118.177 75.1805C118.906 74.1793 119.206 72.9297 119.012 71.7059L114.59 44.0155C114.394 42.7921 113.719 41.6956 112.714 40.9664C111.709 40.2373 110.457 39.935 109.231 40.126L22.761 53.7C21.5357 53.8938 20.4384 54.5649 19.71 55.5661C18.9815 56.5674 18.6813 57.8169 18.8753 59.0407L23.297 86.7311C23.4937 87.9545 24.1685 89.051 25.1733 89.7802C26.178 90.5094 27.4307 90.8116 28.6565 90.6206ZM109.318 40.669C110.399 40.5005 111.504 40.7671 112.391 41.4105C113.278 42.0539 113.873 43.0214 114.047 44.1008L118.468 71.7913C118.639 72.8711 118.375 73.9736 117.732 74.8571C117.089 75.7405 116.121 76.3327 115.04 76.5037L28.5698 90.0776C27.4882 90.2462 26.3829 89.9795 25.4964 89.3361C24.6098 88.6927 24.0144 87.7252 23.8408 86.6458L19.4192 58.9554C19.248 57.8755 19.5128 56.773 20.1556 55.8895C20.7984 55.0061 21.7665 54.4139 22.8477 54.243L109.318 40.669Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M86.9017 56.8698L56.7186 61.608C56.2204 61.6862 55.7736 61.959 55.4775 62.3661C55.1814 62.7731 55.0598 63.2816 55.1392 63.7789C55.2186 64.2762 55.4926 64.7225 55.9011 65.019C56.3096 65.3154 56.8194 65.438 57.3178 65.3599L87.5008 60.6217C87.9992 60.5435 88.4459 60.2707 88.742 59.8637C89.0382 59.4566 89.1597 58.9482 89.0803 58.4508C89.0009 57.9535 88.7268 57.5072 88.3184 57.2107C87.9099 56.9143 87.4001 56.7916 86.9017 56.8698Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M88.0721 64.198L29.8813 73.3327C29.3832 73.411 28.9372 73.6838 28.6411 74.0907C28.3449 74.4977 28.2233 75.0061 28.3028 75.5035C28.3822 76.0008 28.6562 76.4472 29.0647 76.7436C29.473 77.0398 29.9823 77.1627 30.4804 77.0846L88.6713 67.9499C89.1696 67.8716 89.6163 67.5988 89.9124 67.1918C90.2086 66.7847 90.3301 66.2763 90.2507 65.779C90.1713 65.2816 89.8973 64.8353 89.4888 64.5388C89.0803 64.2424 88.5705 64.1198 88.0721 64.198Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M101.054 52.2836C96.713 52.9653 93.7551 57.0315 94.4469 61.3655C95.139 65.6996 99.2193 68.6618 103.561 67.9804C107.903 67.2989 110.861 63.2317 110.169 58.8974C109.477 54.5634 105.396 51.6021 101.054 52.2836Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M30.7385 103.653L117.208 90.0792C118.434 89.8885 119.687 90.1909 120.691 90.92C121.696 91.6491 122.371 92.7454 122.568 93.9686L126.99 121.659C127.183 122.883 126.883 124.132 126.155 125.133C125.426 126.134 124.329 126.806 123.104 127L36.634 140.574C35.4082 140.765 34.1555 140.463 33.1507 139.733C32.1459 139.004 31.4712 137.908 31.2745 136.684L26.8529 108.994C26.6588 107.77 26.9589 106.52 27.6874 105.519C28.4158 104.518 29.5132 103.847 30.7385 103.653ZM31.8183 136.599C31.9916 137.678 32.5869 138.646 33.4736 139.29C34.3602 139.933 35.4657 140.2 36.5473 140.031L123.017 126.457C124.098 126.286 125.066 125.694 125.709 124.81C126.352 123.927 126.617 122.824 126.446 121.744L122.024 94.054C121.85 92.9746 121.255 92.0071 120.369 91.3637C119.482 90.7204 118.377 90.4537 117.295 90.6221L30.8252 104.196C29.7439 104.367 28.7755 104.959 28.1327 105.842C27.4899 106.726 27.2252 107.829 27.3967 108.908L31.8183 136.599Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M94.8783 106.823L64.6952 111.561C64.1969 111.639 63.7502 111.912 63.454 112.319C63.158 112.726 63.0363 113.235 63.1157 113.732C63.1952 114.229 63.4692 114.676 63.8776 114.972C64.2861 115.269 64.796 115.391 65.2943 115.313L95.4774 110.575C95.9757 110.497 96.4224 110.224 96.7186 109.817C97.0147 109.41 97.1363 108.901 97.0569 108.404C96.9775 107.907 96.7034 107.46 96.295 107.164C95.8865 106.867 95.3766 106.745 94.8783 106.823Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M96.0482 114.151L37.8574 123.286C37.3592 123.364 36.9132 123.637 36.6171 124.044C36.321 124.451 36.1994 124.959 36.2788 125.457C36.3583 125.954 36.6323 126.4 37.0408 126.697C37.4491 126.993 37.9584 127.116 38.4565 127.038L96.6473 117.903C97.1457 117.825 97.5923 117.552 97.8885 117.145C98.1847 116.738 98.3062 116.229 98.2268 115.732C98.1474 115.235 97.8733 114.788 97.4649 114.492C97.0564 114.196 96.5466 114.073 96.0482 114.151Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M109.03 102.237C104.689 102.918 101.731 106.985 102.423 111.319C103.115 115.653 107.195 118.615 111.537 117.934C115.879 117.252 118.837 113.185 118.145 108.851C117.453 104.517 113.372 101.555 109.03 102.237Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M88.9488 137.555L176.114 144.687C177.349 144.79 178.489 145.339 179.284 146.213C180.079 147.088 180.463 148.217 180.353 149.352L177.831 175.057C177.718 176.193 177.119 177.241 176.167 177.973C175.215 178.704 173.986 179.059 172.75 178.96L85.5851 171.828C84.3498 171.726 83.2096 171.177 82.4148 170.303C81.6201 169.428 81.2356 168.299 81.3458 167.163L83.8685 141.458C83.9813 140.322 84.5795 139.274 85.5319 138.542C86.4844 137.81 87.7132 137.455 88.9488 137.555ZM81.894 167.208C81.7965 168.21 82.1356 169.206 82.8369 169.978C83.5383 170.75 84.5444 171.234 85.6346 171.324L172.8 178.456C173.89 178.544 174.974 178.231 175.815 177.585C176.655 176.94 177.183 176.014 177.283 175.012L179.805 149.308C179.902 148.305 179.563 147.309 178.862 146.537C178.161 145.766 177.155 145.282 176.065 145.191L88.8993 138.059C87.809 137.971 86.7246 138.284 85.8842 138.93C85.0437 139.575 84.516 140.501 84.4167 141.503L81.894 167.208Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M150.346 154.691L119.92 152.202C119.411 152.16 118.908 152.307 118.523 152.603C118.138 152.899 117.902 153.317 117.858 153.764C117.814 154.212 117.965 154.663 118.286 155.016C118.607 155.37 119.074 155.595 119.583 155.637L150.009 158.126C150.519 158.168 151.022 158.022 151.407 157.726C151.792 157.43 152.028 157.011 152.072 156.564C152.115 156.117 151.965 155.667 151.644 155.313C151.322 154.959 150.856 154.733 150.346 154.691Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M149.679 161.488L91.021 156.689C90.5112 156.647 90.0077 156.794 89.6222 157.09C89.2379 157.386 89.0026 157.804 88.9586 158.251C88.9147 158.699 89.0645 159.149 89.3855 159.503C89.7072 159.857 90.1741 160.082 90.6838 160.124L149.342 164.923C149.852 164.965 150.355 164.819 150.74 164.523C151.125 164.227 151.361 163.808 151.405 163.361C151.448 162.913 151.298 162.463 150.977 162.11C150.655 161.756 150.189 161.53 149.679 161.488Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M165.194 153.745C160.794 153.385 156.949 156.371 156.558 160.358C156.166 164.346 159.369 167.908 163.769 168.268C168.168 168.628 172.014 165.643 172.406 161.655C172.797 157.667 169.594 154.105 165.194 153.745Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M16.8766 224.002L100.364 200.043C101.548 199.705 102.831 199.812 103.933 200.341C105.034 200.87 105.864 201.778 106.24 202.865L114.715 227.485C115.088 228.574 114.976 229.751 114.405 230.761C113.833 231.77 112.847 232.528 111.665 232.869L28.177 256.829C26.9932 257.168 25.7098 257.061 24.6082 256.531C23.5067 256.002 22.677 255.094 22.3012 254.007L13.826 229.387C13.4527 228.298 13.5643 227.12 14.1362 226.111C14.7081 225.101 15.6937 224.343 16.8766 224.002ZM22.8263 253.856C23.1576 254.816 23.8897 255.617 24.8617 256.084C25.8337 256.551 26.9663 256.645 28.0108 256.346L111.499 232.387C112.542 232.086 113.412 231.417 113.916 230.526C114.421 229.636 114.52 228.596 114.19 227.636L105.715 203.016C105.383 202.056 104.651 201.255 103.679 200.788C102.707 200.321 101.575 200.227 100.531 200.525L17.0428 224.485C15.9989 224.785 15.1291 225.454 14.6244 226.345C14.1197 227.236 14.0214 228.276 14.3511 229.236L22.8263 253.856Z"
                    fill="#5331C6"
                  />
                  <path
                    d="M80.7735 218.308L51.6317 226.671C51.1452 226.811 50.7433 227.122 50.5119 227.53C50.2809 227.938 50.2385 228.409 50.3867 228.839C50.535 229.27 50.8647 229.634 51.3095 229.847C51.7551 230.061 52.2794 230.105 52.7658 229.966L81.9076 221.603C82.394 221.463 82.7963 221.153 83.0277 220.745C83.2587 220.337 83.301 219.866 83.1529 219.436C83.0047 219.005 82.675 218.641 82.2301 218.428C81.7845 218.214 81.26 218.168 80.7735 218.308Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M83.0156 224.822L26.8317 240.946C26.3454 241.085 25.9441 241.396 25.7128 241.804C25.4818 242.212 25.4394 242.683 25.5876 243.113C25.7359 243.544 26.0656 243.908 26.5104 244.121C26.9558 244.335 27.4796 244.38 27.9658 244.24L84.1498 228.116C84.6362 227.977 85.0385 227.667 85.2699 227.258C85.5009 226.851 85.5432 226.38 85.3951 225.949C85.2469 225.519 84.9171 225.155 84.4723 224.941C84.0267 224.727 83.5022 224.682 83.0156 224.822Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                  <path
                    d="M94.0882 212.233C89.8792 213.441 87.579 217.543 88.8955 221.368C90.2122 225.193 94.6695 227.357 98.8786 226.149C103.088 224.941 105.388 220.838 104.071 217.013C102.754 213.188 98.2972 211.025 94.0882 212.233Z"
                    stroke="#5331C6"
                    strokeWidth="0.6"
                  />
                </svg>
              </div>
            </div>
            {domainProgress.hasSavedDomain && (
              <div className="w-[890px] max-w-full">
                <DeveloperSetupShareCard devInstructionsId={org?.devInstructionsId} />
              </div>
            )}
          </div>

          <div className="mt-[32px] flex flex-col items-center pt-[100px] pb-[32px]">
            <div className="min-w-[160px] flex justify-center">
              <Button
                color="primary"
                onClick={async () => {
                  await createDefaultCollectionGroups();
                  if (!setupComplete) {
                    captureProductAnalyticsEvent("onboarding_step_skipped", {
                      step_id: "domain_email_setup",
                      section: "domain_email_setup",
                      skip_reason: "skip_button",
                      setup_mode: domainProgress.setupMode,
                    });
                  }
                  captureProductAnalyticsEvent("onboarding_flow_completed", {
                    completion_mode: setupComplete ? "verified" : "skipped",
                    skipped_step_ids: setupComplete ? [] : ["domain_email_setup"],
                    setup_mode: domainProgress.setupMode,
                  });
                  history.push(Routes.getCompliant.businessSurveyAquilo);
                }}
              >
                {setupComplete ? "Next" : "Skip"}
              </Button>
            </div>
            <button
              type="button"
              onClick={() => {
                captureProductAnalyticsEvent("onboarding_flow_exited", {
                  exit_reason: "back_to_invite_users",
                  current_step_id: "domain_email_setup",
                  setup_mode: domainProgress.setupMode,
                });
                history.push(Routes.getCompliant.inviteUsers);
              }}
              className="mt-[10px] flex items-center gap-[6px] text-[14px] font-medium text-neutral-500"
            >
              <KeyboardArrowLeft className="w-[20px] h-[20px] text-neutral-400" />
              Back to Invite Teammates
            </button>
          </div>
        </div>
      </div>
    </OmniBarProvider>
  );
}

export function CustomDomainIntroInstructions() {
  return (
    <div className="text-[18px] font-medium leading-[28px] text-neutral-900">
      Follow the instructions below to set up your custom domain for your Privacy Center. First time
      adding a subdomain?{" "}
      <DomainSetupDocumentationLink href="https://knowledge.truevault.com/article/212-how-to-set-up-a-custom-domain-using-a-cname">
        Here's a how-to guide.
      </DomainSetupDocumentationLink>
    </div>
  );
}

export function CustomDomainStepByStepInstructions({
  privacyCenter,
  stepNumberBgColor,
  stepNumberTextColor,
}: {
  privacyCenter: PrivacyCenterDto;
  stepNumberBgColor?: string; // tailwind class name, e.g. "bg-primary-100"
  stepNumberTextColor?: string; // tailwind class name, e.g. "text-primary-600"
}) {
  return (
    <div className="mt-[12px] space-y-[12px] text-[14px]">
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="1" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          Login to your domain host (for example: Google Domains, GoDaddy, Hover and similar).
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="2" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>Click on your company's domain.</div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="3" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          Find the menu which says <strong>DNS</strong> or <strong>DNS Records</strong> and click on
          it.
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="4" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          Click <strong>Add New Record</strong> (it might say “Create new record” or something
          similar).
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="5" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          Select <strong>CNAME</strong> as the <strong>Type</strong>.
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="6" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div className="flex w-full">
          <span className="mr-[12px]">
            At <strong>CNAME Host Name</strong>:{" "}
          </span>
          <CodeSnippet inline={true}>privacy</CodeSnippet>
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="7" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          <div>
            Copy and paste the value below under <strong>Value</strong> (it might also be called{" "}
            <strong>Data</strong> or <strong>CNAME Destination</strong>).
          </div>
          <div className="flex w-full">
            <span className="mr-[12px]">
              <strong>CNAME Value</strong>:{" "}
            </span>
            <CodeSnippet inline={true}>{getPrivacyCenterCnameValue(privacyCenter)}</CodeSnippet>
          </div>
        </div>
      </div>
      <div className="flex gap-[12px]">
        <div>
          <StepNumberCircle text="8" bgColor={stepNumberBgColor} textColor={stepNumberTextColor} />
        </div>
        <div>
          Click <strong>Save</strong>.
        </div>
      </div>
    </div>
  );
}

export function VerifySubdomain({
  privacyCenter,
  privacyCenterDomain,
  setPrivacyCenterDomain,
  inputWidth,
  maxWidth,
  onHandleSkip,
  onSuccess,
  onError,
}: {
  privacyCenter: PrivacyCenterDto;
  privacyCenterDomain: string;
  setPrivacyCenterDomain: (domain: string) => void;
  inputWidth?: string;
  maxWidth?: string;
  onHandleSkip?: (checked: boolean) => void; // if undefined/null, then won't show checkbox to skip if subdomain verification fails
  onSuccess?: () => void;
  onError?: () => void;
}) {
  const [subdomainError, setSubdomainError] = useState<boolean | string>("");
  const [subdomainSuccessful, setSubdomainSuccessful] = useState<boolean>(false);
  const [subdomainLoading, setSubdomainLoading] = useState<boolean>(false);

  const verifyPrivacyCenterDomain = async (domain: string): Promise<void> => {
    setSubdomainLoading(true);
    setSubdomainSuccessful(false);
    setSubdomainError("");
    try {
      const res = await Api.privacyCenter.verifyPrivacyCenterDomain(
        privacyCenter.organizationId,
        privacyCenter.id,
        domain,
      );
      if (res) {
        setPrivacyCenterDomain(domain);
        setSubdomainSuccessful(true);

        if (onSuccess) {
          onSuccess();
        }
      } else {
        setSubdomainError(res === false ? domain : true);

        if (onError) {
          onError();
        }
      }
    } catch {
      setSubdomainError(true);
      if (onError) {
        onError();
      }
    } finally {
      setSubdomainLoading(false);
    }
  };

  return (
    <>
      <Form
        initialValues={{ domain: privacyCenterDomain || "" }}
        onSubmit={(values) => {
          verifyPrivacyCenterDomain(values.domain);
        }}
        render={({ handleSubmit, values, dirty }) => {
          return (
            <form onSubmit={handleSubmit}>
              <div className={clsx(maxWidth)}>
                <div className="flex-row">
                  <Field name="domain">
                    {(props) => (
                      <TextInput
                        field="domain"
                        onChange={props.input.onChange}
                        defaultValue={privacyCenterDomain}
                        disabled={subdomainLoading}
                        inputWidth={inputWidth || "w-[550px]"}
                        inputTextSize="text-[14px] text-neutral-800"
                      />
                    )}
                  </Field>
                  <Button
                    color="tertiary"
                    disabled={!values.domain || subdomainLoading || !dirty}
                    onClick={handleSubmit}
                  >
                    Verify Subdomain
                  </Button>
                </div>
                <div className="mt-[12px]">
                  {subdomainSuccessful && !subdomainLoading && !subdomainError && (
                    <div className="bg-green-100 rounded-[6px] px-[12px] py-[7px] flex items-center justify-center">
                      <div>
                        <CheckCircleIcon className="w-[16px] h-[16px] text-green-400" />
                      </div>
                      <div className="ml-[12px] text-green-800 font-medium text-[12px]">
                        Great! Your subdomain is verified.
                      </div>
                    </div>
                  )}
                  {subdomainError === true && (
                    <div className="bg-red-50 rounded-[6px] px-[12px] py-[7px] flex">
                      <div className="mt-[4px]">
                        <XCircleIcon className="w-[16px] h-[16px] text-red-400" />
                      </div>
                      <div className="ml-[12px] text-[12px] leading-[24px]">
                        <div className="text-red-800 font-medium">
                          Subdomain could not be verified
                        </div>
                        <div className="text-red-500 font-normal">
                          Oops! Looks like your subdomain is not set up. Please follow the
                          instructions above to set it up and retry verification.
                        </div>
                      </div>
                    </div>
                  )}
                  {subdomainError && subdomainError !== true && (
                    <div className="bg-red-50 rounded-[6px] px-[12px] py-[7px] flex">
                      <div className="mt-[4px]">
                        <XCircleIcon className="w-[16px] h-[16px] text-red-400" />
                      </div>
                      <div className="ml-[12px] text-[12px]">
                        <div className="text-red-800 font-medium">
                          Subdomain could not be verified
                        </div>
                        <div className="text-red-500 font-normal">
                          Oops! Looks like {subdomainError} points to the wrong CNAME. Copy and
                          paste the value at item 7 above as the CNAME Destination and retry
                          verification.
                        </div>
                      </div>
                    </div>
                  )}
                  {subdomainError != "" && onHandleSkip && (
                    <DescriptionCheckbox
                      color="primary"
                      label="Certain DNS providers may take additional time to propagate a new CNAME record. Check this box to proceed with onboarding and verify later if your DNS propagation is going slowly."
                      onChange={(_, checked) => onHandleSkip(checked)}
                    />
                  )}
                </div>
              </div>
            </form>
          );
        }}
      />
    </>
  );
}
